import { useQuery } from '@tanstack/react-query';
import { useId, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { PageContainer } from '../app/AppLayout';
import { useApi } from '../api/context';
import {
  queryKeys,
  useActiveRuns,
  useDeployment,
  useNetworkPage,
  usePager,
  useRunList,
} from '../api/queries';
import type { RunKind, RunListFilter, RunSummary } from '../api/types';
import { ErrorMessage, Loading, PageHeader, Pager } from '../components/ui';
import { ListActivityWindow } from './ActivityWindow';
import { strings } from '../strings';
import { listLimit, withParam } from '../lib/listParams';
import { useNetworking } from '../networks/shared';
import { RunSummaryTable } from './RunSummaryTable';

const s = strings.runsList;

const STATUS_FILTERS = ['active', 'completed', 'failed', 'canceled'] as const;
type StatusFilter = (typeof STATUS_FILTERS)[number];
const KINDS: readonly RunKind[] = ['plan', 'apply', 'destroy', 'refresh'];
const TARGETS = ['deployment', 'network'] as const;
const n = strings.networks.runs;

function oneOf<T extends string>(value: string | null, allowed: readonly T[]): T | null {
  return value !== null && (allowed as readonly string[]).includes(value) ? (value as T) : null;
}

/** The deployments to filter by: the first page of `GET /deployments`, plus the one the URL names. */
function DeploymentFilter({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (id: string | null) => void;
}) {
  const api = useApi();
  const id = useId();
  const list = useQuery({
    queryKey: queryKeys.deploymentList({}, 'options'),
    queryFn: () => api.listAllDeployments({}, { limit: 200 }),
  });
  const named = useDeployment(value ?? '', { enabled: !!value });
  const options = (list.data?.items ?? []).map((d) => ({
    id: d.id,
    label: `${d.name} · ${d.blueprintName}`,
  }));
  if (value && !options.some((o) => o.id === value)) {
    options.unshift({ id: value, label: named.data?.data.name ?? value });
  }
  return (
    <div className="filter">
      <label htmlFor={id}>{s.deploymentFilter}</label>
      <select
        id={id}
        className="input"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
      >
        <option value="">{strings.common.any}</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

/** The networks to filter by (feature spec 17): the first page of `GET /networks`, plus the one the URL names. */
function NetworkFilter({ value, onChange }: { value: string | null; onChange: (id: string | null) => void }) {
  const id = useId();
  const list = useNetworkPage(null);
  const options = (list.data?.items ?? []).map((network) => ({ id: network.id, label: network.name }));
  if (value && !options.some((o) => o.id === value)) options.unshift({ id: value, label: value });
  return (
    <div className="filter">
      <label htmlFor={id}>{n.networkFilter}</label>
      <select
        id={id}
        className="input"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
      >
        <option value="">{strings.common.any}</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function History({
  filter,
  limit,
  labelledBy,
  pendingRuns,
}: {
  filter: RunListFilter;
  limit: number;
  labelledBy: string;
  pendingRuns: RunSummary[];
}) {
  const pager = usePager();
  const page = useRunList(filter, pager.cursor, true, limit);
  if (page.error) return <ErrorMessage error={page.error} onRetry={() => void page.refetch()} />;
  if (!page.data) return <Loading />;
  const filtered =
    !!filter.kind ||
    !!filter.deploymentId ||
    !!filter.target ||
    !!filter.networkId ||
    filter.status !== 'finished';
  return (
    <>
      {page.data.items.length === 0 ? (
        <p className="help">{filtered && !pager.hasPrevious ? s.noMatch : s.empty}</p>
      ) : (
        <RunSummaryTable
          runs={page.data.items}
          showDeployment
          labelledBy={labelledBy}
          pendingRuns={pendingRuns}
        />
      )}
      <Pager
        hasPrevious={pager.hasPrevious}
        nextCursor={page.data.nextCursor}
        onPrevious={pager.previous}
        onNext={pager.next}
      />
    </>
  );
}

/** `/runs` (spec 6.9): active runs pinned first, then the history, with filters in the URL. */
export function RunsListPage() {
  const [search, setSearch] = useSearchParams();
  const status = oneOf<StatusFilter>(search.get('status'), STATUS_FILTERS);
  const kind = oneOf(search.get('kind'), KINDS);
  const deploymentId = search.get('deployment') || null;
  // Feature spec 17: the Target and Network filters, only while networking is on.
  const { enabled: networking } = useNetworking();
  const target = networking ? oneOf(search.get('target'), TARGETS) : null;
  const networkId = networking ? search.get('network') || null : null;
  const targetId = useId();
  const limit = listLimit(search.get('limit'));
  const statusId = useId();
  const kindId = useId();
  const activeHeading = useId();
  const historyHeading = useId();
  const active = useActiveRuns();

  const setParam = (name: string, value: string | null) =>
    setSearch(withParam(search, name, value), { replace: true });

  const activeRuns = (active.data?.items ?? []).filter(
    (run) =>
      (!kind || run.kind === kind) &&
      (!deploymentId || run.deploymentId === deploymentId) &&
      (!target || (target === 'network') === (!!run.networkId && !run.deploymentId)) &&
      (!networkId || run.networkId === networkId),
  );
  const showActive = status === null || status === 'active';
  const showHistory = status !== 'active';
  const historyFilter = useMemo<RunListFilter>(
    () => ({
      status: status ?? 'finished',
      kind,
      deploymentId,
      ...(target ? { target } : {}),
      ...(networkId ? { networkId } : {}),
    }),
    [status, kind, deploymentId, target, networkId],
  );

  return (
    <PageContainer>
      <PageHeader title={s.title}>
        <p className="mt-0.5 text-fg-muted">{networking ? n.subtitle : s.subtitle}</p>
      </PageHeader>
      <div role="group" aria-label={s.filters} className="list-toolbar">
        <div className="filter">
          <label htmlFor={statusId}>{s.statusFilter}</label>
          <select
            id={statusId}
            className="input"
            value={status ?? ''}
            onChange={(e) => setParam('status', e.target.value || null)}
          >
            <option value="">{strings.common.any}</option>
            {STATUS_FILTERS.map((value) => (
              <option key={value} value={value}>
                {s.statusValues[value]}
              </option>
            ))}
          </select>
        </div>
        <div className="filter">
          <label htmlFor={kindId}>{s.kindFilter}</label>
          <select
            id={kindId}
            className="input"
            value={kind ?? ''}
            onChange={(e) => setParam('kind', e.target.value || null)}
          >
            <option value="">{strings.common.any}</option>
            {KINDS.map((value) => (
              <option key={value} value={value}>
                {strings.status.runKind[value]}
              </option>
            ))}
          </select>
        </div>
        <DeploymentFilter value={deploymentId} onChange={(id) => setParam('deployment', id)} />
        {networking && (
          <>
            <div className="filter">
              <label htmlFor={targetId}>{n.targetFilter}</label>
              <select
                id={targetId}
                className="input"
                value={target ?? ''}
                onChange={(e) => setParam('target', e.target.value || null)}
              >
                <option value="">{strings.common.any}</option>
                {TARGETS.map((value) => (
                  <option key={value} value={value}>
                    {n.targetValues[value]}
                  </option>
                ))}
              </select>
            </div>
            <NetworkFilter value={networkId} onChange={(id) => setParam('network', id)} />
          </>
        )}
      </div>

      {showActive && (
        <section aria-labelledby={activeHeading} className="mb-6">
          <h2 id={activeHeading} className="mb-2 text-[15px] font-semibold">
            {s.active}
          </h2>
          {active.error ? (
            <ErrorMessage error={active.error} onRetry={() => void active.refetch()} />
          ) : !active.data ? (
            <Loading />
          ) : activeRuns.length === 0 ? (
            <p className="help">{s.nothingRunning}</p>
          ) : (
            <RunSummaryTable
              runs={activeRuns}
              showDeployment
              labelledBy={activeHeading}
              pendingRuns={active.data.items}
            />
          )}
        </section>
      )}

      {showHistory && (
        <section aria-labelledby={historyHeading}>
          <h2 id={historyHeading} className="mb-2 text-[15px] font-semibold">
            {s.history}
          </h2>
          <History
            key={JSON.stringify([historyFilter, limit])}
            filter={historyFilter}
            limit={limit}
            labelledBy={historyHeading}
            pendingRuns={active.data?.items ?? []}
          />
        </section>
      )}
      <ListActivityWindow />
    </PageContainer>
  );
}
